<template>
  <main class="bg-white min-h-screen flex flex-col">
    <div class="flex flex-grow max-md:flex-col">
      <aside
        v-if="pendingCompanyDetails"
        class="flex flex-col w-1/5 max-md:ml-0 max-md:w-full bg-primary-purple"
      >
        <div class="flex flex-col mt-14 flex-grow">
          <div class="flex flex-col ml-10 text-left">
            <!-- First Image -->
            <span class="text-white text-3xl font-semibold leading-9">
              Get Started <br />
            </span>
          </div>

          <StepsList :currentStep="0" />
        </div>
      </aside>
      <section
        :class="[
          'flex flex-col justify-center mt-10 pt-6 pb-1 max-md:w-full',
          pendingCompanyDetails ? 'w-4/5' : 'w-full',
        ]"
      >
        <div class="flex justify-center mb-4">
          <div
            v-if="errorMessage"
            role="alert"
            class="flex gap-2 w-1/2 justify-center p-2.5 mt-6 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
            style="max-height: 80px; overflow-y: auto"
          >
            <!-- Icon Container -->
            <div
              class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
            >
              <!-- Custom Image Icon -->
              <img
                src="../assets/shared/failure.svg"
                alt="Error Icon"
                class="w-4 h-4"
              />
            </div>
            <p>{{ errorMessage }}</p>
          </div>
        </div>
        <div
          class="flex flex-col w-1/2 mx-auto justify-center max-md:mt-4 max-md:w-full"
        >
          <h1
            class="text-3xl font-semibold leading-9 text-center text-zinc-800"
          >
            Personal Details
          </h1>
          <p class="mt-3 text-base leading-6 text-center text-neutral-700">
            Add your personal details to get started
          </p>
          <form @submit.prevent="handleSubmit" class="flex flex-col gap-2">
            <div class="mt-6 text-left">
              <BaseLabel
                forAttr="firstName"
                text="First Name"
                class="text-sm font-medium leading-5 text-zinc-800"
              />
              <BaseInput
                id="firstName"
                v-model="firstName"
                placeholder="John"
                className="px-3.5 py-2.5 mt-1 text-sm leading-5 text-gray-900 whitespace-nowrap bg-white rounded-md border border-gray-300 border-solid shadow-sm max-md:pr-5 w-full"
                required
              />
            </div>
            <div class="mt-6 text-left">
              <BaseLabel
                forAttr="lastName"
                text="Last Name"
                class="text-sm font-medium leading-5 text-zinc-800"
              />
              <BaseInput
                id="lastName"
                v-model="lastName"
                placeholder="Doe"
                className="px-3.5 py-2.5 mt-1 text-sm leading-5 text-gray-900 whitespace-nowrap bg-white rounded-md border border-gray-300 border-solid shadow-sm max-md:pr-5 w-full"
                required
              />
            </div>
            <div class="mt-6 text-left" v-if="pendingCompanyDetails">
              <BaseLabel
                forAttr="companyName"
                text="Company Name"
                class="text-sm font-medium leading-5 text-zinc-800"
              />
              <BaseInput
                id="companyName"
                v-model="companyName"
                placeholder="Acme Corporation"
                className="px-3.5 py-2.5 mt-1 text-sm leading-5 text-gray-900 whitespace-nowrap bg-white rounded-md border border-gray-300 border-solid shadow-sm max-md:pr-5 w-full"
                required
              />
            </div>
            <div
              v-if="pendingCompanyDetails"
              class="flex flex-col mt-6 w-full text-sm leading-none text-left"
            >
              <label for="softwareUrl" class="font-medium text-zinc-800"
                >Company Website</label
              >
              <div
                class="flex overflow-hidden items-center mt-1 w-full text-gray-500 whitespace-nowrap rounded-md border border-gray-300 border-solid"
              >
                <span
                  class="self-stretch py-2.5 pr-3 pl-3.5 bg-gray-100 rounded-md"
                  >https://</span
                >
                <input
                  type="text"
                  id="url"
                  v-model="companyWebsite"
                  class="overflow-hidden flex-1 shrink self-stretch py-2.5 pr-3.5 pl-3 my-auto bg-white rounded-none"
                  placeholder="www.example.com"
                  required
                />
              </div>
            </div>
            <div class="flex flex-col flex-grow mt-6 basis-1/2">
              <BaseLabel
                forAttr="title"
                text="Your Title"
                class="text-sm font-medium leading-5 text-zinc-800 text-left"
              />
              <BaseInput
                type="text"
                id="title"
                v-model="title"
                placeholder="Your title"
                className="flex-grow px-3.5 py-2.5 mt-1 text-sm leading-5 text-gray-900 whitespace-nowrap bg-white rounded-md border border-gray-300 border-solid shadow-sm max-md:pr-5 w-full"
                required
              />
            </div>
          </form>
        </div>
        <FooterSection :isDisabled="isDisabled" @next="goNext" />
      </section>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, getCurrentInstance, ref, onMounted } from "vue";
import FooterSection from "@/components/expert-program/FooterSection.vue";
import BaseLabel from "@/components/shared/BaseLabel.vue";
import BaseInput from "@/components/shared/BaseInput.vue";
import StepsList from "@/components/expert-program/StepsList.vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import { useFrontegg } from "@frontegg/vue";

const UPDATE_COMPANY_USER = gql`
  mutation updateCompanyUser($input: UpdateCompanyUserInput!) {
    updateCompanyUser(input: $input) {
      success
      errors
    }
  }
`;

export default defineComponent({
  name: "AddCompanyDetails",
  components: {
    BaseLabel,
    BaseInput,
    FooterSection,
    StepsList,
  },

  setup() {
    const companyName = ref("");
    const firstName = ref("");
    const lastName = ref("");
    const title = ref("");
    const companyWebsite = ref("");
    const errorMessage = ref<string | null>(null);
    const userStore = useUserStore();
    const tempDisable = ref(true);
    const isDisabled = ref(false);
    const router = useRouter();
    const route = useRoute();
    const pendingCompanyDetails = route.query.pendingCompanyDetails === "true";
    const { loginWithRedirect } = useFrontegg();

    onMounted(() => {
      if (userStore.token) {
        firstName.value = userStore.firstName || "";
        lastName.value = userStore.lastName || "";
        title.value = userStore.title || "";
        companyName.value = userStore.companyName || "";
        companyWebsite.value = userStore.companyWebsite || "";
      } else {
        loginWithRedirect();
      }
    });

    const validateInput = () => {
      if (!firstName.value) {
        errorMessage.value = "Please provide first name";
        return false;
      }
      if (!lastName.value) {
        errorMessage.value = "Please provide last name";
        return false;
      }
      if (!title.value) {
        errorMessage.value = "Please provide title";
        return false;
      }

      if (!pendingCompanyDetails) {
        return true;
      }

      if (!companyName.value) {
        errorMessage.value = "Please provide company name";
        return false;
      }
      if (!companyWebsite.value) {
        errorMessage.value = "Please provide company website";
        return false;
      }

      return true;
    };

    const goNext = async () => {
      isDisabled.value = true;
      errorMessage.value = null;
      if (!validateInput()) {
        isDisabled.value = false;
        return;
      }

      isDisabled.value = true;
      const {
        mutate: updateCompanyUser,
        onDone: updateCompanyUserDone,
        onError: updateCompanyUserError,
      } = useMutation(UPDATE_COMPANY_USER);

      updateCompanyUser({
        input: {
          companyUserParams: {
            firstName: firstName.value,
            lastName: lastName.value,
            title: title.value,
          },
          ...(pendingCompanyDetails && {
            companyParams: {
              name: companyName.value,
              website: formatWebsite(companyWebsite.value),
            },
          }),
        },
      });

      updateCompanyUserDone((result) => {
        isDisabled.value = false;
        if (result) {
          updateAuthStore();
          navigateToOnboarding();
        }
      });
      updateCompanyUserError((error) => {
        isDisabled.value = false;
        errorMessage.value = error.message;
      });
    };

    const updateAuthStore = () => {
      userStore.setFirstName(firstName.value);
      userStore.setLastName(lastName.value);
      userStore.setTitle(title.value);
      userStore.setCompanyName(companyName.value);
      userStore.setCompanyWebsite(companyWebsite.value);
    };

    const navigateToOnboarding = async () => {
      await router.push({ name: "SignInLanding" });
    };

    const formatWebsite = (url: string) => {
      const trimmedUrl = url.trim();
      return trimmedUrl.startsWith("https://")
        ? trimmedUrl
        : `https://${trimmedUrl}`;
    };

    return {
      firstName,
      lastName,
      companyName,
      companyWebsite,
      isDisabled,
      title,
      errorMessage,
      tempDisable,
      goNext,
      pendingCompanyDetails,
    };
  },
});
</script>
